import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_REGION, buildRegionPath, useActiveRegion } from '../regions';
import {
  BuildingOffice2Icon,
  BoltIcon,
  LightBulbIcon,
  TruckIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { publicAPI } from '../services/api';
import { LandingStats } from '../types';
import maExpansionMap from '../assets/screenshots/map.avif';
import landingHeroScreenshot from '../assets/screenshots/landing-hero-20251007.avif';
import broomIcon from '../assets/icons/broom.png';
import lawnMowerIcon from '../assets/icons/lawn-mower.png';
import { canonicalUrlFor, metaDescription } from '../utils/seo';
import SEO from '../components/SEO';

const FALLBACK_STATS: LandingStats = {
  active_opportunities: 153,
  ma_cities: 6,
  ma_procuring_orgs: 6,
  service_industries: 35,
};

type ServiceCard = {
  title: string;
  icon?: React.ComponentType<React.ComponentProps<'svg'>>;
  imageSrc?: string;
};

const PngIcon: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="h-8 w-8 object-contain"
    width={32}
    height={32}
    loading="lazy"
  />
);

const SERVICE_CARDS: ServiceCard[] = [
  { title: 'Construction and Contracting', icon: BuildingOffice2Icon },
  { title: 'Landscaping & Snow', imageSrc: lawnMowerIcon },
  { title: 'Electrical', icon: BoltIcon },
  { title: 'Janitorial & Facilities', imageSrc: broomIcon },
  { title: 'Professional Services', icon: LightBulbIcon },
  { title: 'Transportation', icon: TruckIcon },
];

const whatsNextItems = [
  'We want to expand to commercial opportunities: universities, business parks, etc.',
  'Richer data on the business opportunity landscape across the state.',
  'A blog with relevant content to help you find and win contracts.',
  "Have feedback on what you'd like OppFoundry to do? We want to hear from you!"
];

const Landing: React.FC = () => {
  const activeRegion = useActiveRegion() ?? DEFAULT_REGION;
  const browseOpportunitiesPath = buildRegionPath(activeRegion, 'opportunities');
  const [landingStats, setLandingStats] = useState<LandingStats | null>(null);

  useEffect(() => {
    let isMounted = true;

    publicAPI
      .getLandingStats()
      .then(({ data }) => {
        if (isMounted) {
          setLandingStats(data);
        }
      })
      .catch(() => {
        // Swallow errors and fall back to default copy
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const stats = landingStats ?? FALLBACK_STATS;

  const heroStats = [
    { label: 'Active Opportunities', value: stats.active_opportunities },
    { label: 'Massachusetts Procuring Organizations', value: stats.ma_procuring_orgs ?? stats.ma_cities },
    { label: 'Service Industries', value: stats.service_industries },
  ];

  return (
    <div className="bg-white">
      <SEO
        title={activeRegion === 'ma' ? 'OppFoundry – Public Opportunities in Massachusetts' : 'OppFoundry – Public Opportunities'}
        description={metaDescription('Discover relevant public opportunities for service businesses across Massachusetts. Filter by procuring organization, industry, and due date.')}
        canonical={canonicalUrlFor()}
        og={{
          title: 'OppFoundry – Public Opportunities',
          description: metaDescription('Find and track municipal opportunities for your business.'),
          url: canonicalUrlFor() ?? undefined,
        }}
      />
      {/* Hero: What it is + value */}
      <section className="mx-auto w-full max-w-6xl px-4 py-12 sm:py-20">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-5xl">
          Grow your service business in Massachusetts
        </h1>
        <div className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-start">
          <div className="lg:w-1/2">
            <p className="text-base text-gray-600 sm:text-lg">
              OppFoundry is built for service businesses: electricians, landscapers, plumbers, contractors, professional service consultants, and more. Discover relevant business opportunities in Massachusetts fast and gain an edge over your competition.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={browseOpportunitiesPath}
                className="rounded-md bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-blue-500 hover:text-white"
              >
                Browse All Opportunities
              </Link>
              <Link
                to="/signup"
                className="rounded-md border border-blue-600 px-6 py-3 text-base font-semibold text-blue-600 hover:bg-blue-50"
              >
                Sign Up
              </Link>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 lg:w-1/2 lg:justify-end">
              {heroStats.map((stat) => (
                <div
                  key={stat.label}
                  className="min-w-[160px] flex-1 rounded-xl bg-white p-6 shadow-[0_10px_24px_rgba(167,139,250,0.32)] ring-1 ring-purple-100 sm:flex-none sm:basis-0"
                >
                  <p className="text-3xl font-semibold text-blue-600">{stat.value.toLocaleString()}</p>
                  <p className="mt-1 text-sm font-medium text-gray-600">{stat.label}</p>
                </div>
              ))}
            </div>
        </div>
        <div className="mt-16">
          <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
            Built for service businesses
          </h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_CARDS.map(({ title, icon: Icon, imageSrc }) => (
              <div
                key={title}
                className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-[0_10px_24px_rgba(167,139,250,0.32)]"
              >
                {imageSrc ? (
                  <PngIcon src={imageSrc} alt={title} />
                ) : (
                  Icon && <Icon className="h-6 w-6 text-blue-600" />
                )}
                <div>
                  <p className="font-semibold text-gray-900">{title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-16">
          <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
            OppFoundry makes it easy to find opportunities for your business
          </h2>
          <div className="relative mt-6 overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-100">
            <img
              src={landingHeroScreenshot}
              alt="OppFoundry contracts dashboard"
              className="h-auto w-full object-cover"
              width={1600}
              height={1088}
              loading="lazy"
              decoding="async"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-black/10 to-transparent"
            />
          </div>
        </div>
      </section>

      {/* Screenshots with captions (placeholder blocks) */}
      {false && (
        <section className="mx-auto w-full max-w-6xl px-4 py-12">
          <h2 className="text-2xl font-semibold text-gray-900">A quick look</h2>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {Array.from({ length: 2 }).map((_, idx) => (
              <div key={idx} className="h-64 overflow-hidden rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100" />
            ))}
          </div>
        </section>
      )}

      {/* Future plans and city coverage */}
      <section className="mx-auto w-full max-w-6xl px-4 py-12">
        <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">What’s next</h2>
        <p className="mt-4 text-sm text-gray-600">
          We’ve got a lot planned, from more procuring organizations added each week to richer features and functionality that makes it easier for you to grow your business.
        </p>
        <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:items-stretch">
          <div className="flex h-full flex-col">
            <ul className="flex h-full flex-col justify-between gap-4">
              {whatsNextItems.map((item, idx) => (
                <li
                  key={idx}
                  className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <p className="text-sm text-slate-600">{item}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex h-full items-stretch overflow-hidden rounded-2xl">
            <img
              src={maExpansionMap}
              alt="Map highlighting Massachusetts expansion coverage"
              className="h-full w-full object-cover"
              width={768}
              height={768}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/**
       * Testimonials placeholder – keep structure for future launch once real quotes exist.
       */}
      {false && (
        <section className="mx-auto w-full max-w-6xl px-4 py-12">
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
            <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">OppFoundry User Testimonials</h2>
          </div>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {[`“This saves me a couple hours a week.”`, `“Finally, everything in one place.”`, `“Clear deadlines and filters make it easy to act.”`].map((q, i) => (
              <div key={i} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <p className="text-sm text-gray-700">{q}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Landing;
